import React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Star, Briefcase, Clock, Home, ChevronRight, CheckCircle2 } from 'lucide-react';
import { MobileLayout } from '../../components/MobileLayout';
import { Card, CardContent } from '../../components/ui/Card';
import { useScreenInit } from '../../useScreenInit.js';
import { usePlatform, initials } from '../../context/PlatformContext';
const availabilityMeta: Record<string, {
  label: string;
  cls: string;
  dot: string;
}> = {
  disponibile: {
    label: 'Disponibile',
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dot: 'bg-emerald-500'
  },
  occupato: {
    label: 'Occupato',
    cls: 'bg-amber-50 text-amber-700 border-amber-200',
    dot: 'bg-amber-500'
  },
  off: {
    label: 'Off',
    cls: 'bg-gray-100 text-gray-500 border-gray-200',
    dot: 'bg-gray-400'
  }
};
const jobStatusMeta: Record<string, {
  label: string;
  cls: string;
}> = {
  da_assegnare: {
    label: 'Da assegnare',
    cls: 'bg-amber-50 text-amber-700 border-amber-200'
  },
  assegnata: {
    label: 'Assegnata',
    cls: 'bg-muted text-muted-foreground border-border'
  },
  in_corso: {
    label: '● In corso',
    cls: 'bg-coral/5 text-coral border-coral/20'
  },
  completata: {
    label: 'Completata',
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  }
};
export function CmTeamCleanerDettaglioPage() {
  useScreenInit();
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    cleanerById,
    jobsForCleaner,
    cleanerAvgScore
  } = usePlatform();
  const cleaner = cleanerById(id);
  if (!cleaner) {
    return <MobileLayout data-id="element-3033">
        <div className="text-center py-20" data-id="element-3034">
          <p className="text-sm text-muted-foreground mb-4" data-id="element-3035">
            Cleaner non trovato.
          </p>
          <Link to="/cm/team" className="inline-flex items-center gap-1.5 text-sm font-medium text-coral hover:underline" data-id="element-3036">
            <ArrowLeft size={15} data-id="element-3037" />
            Torna al team
          </Link>
        </div>
      </MobileLayout>;
  }
  const jobs = jobsForCleaner(cleaner.id);
  const attivi = jobs.filter(j => j.status === 'assegnata' || j.status === 'in_corso');
  const completati = jobs.filter(j => j.status === 'completata');
  const avg = cleanerAvgScore(cleaner.name);
  const meta = availabilityMeta[cleaner.availability];
  return <MobileLayout data-id="element-3038">
      <Link to="/cm/team" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-navy transition-colors mb-4" data-id="element-3039">
        <ArrowLeft size={16} data-id="element-3040" />
        Team
      </Link>

      {/* Header */}
      <div className="bg-white border border-border rounded-xl shadow-sm p-4 lg:p-5 mb-6" data-id="element-3041">
        <div className="flex items-start gap-4" data-id="element-3042">
          <span className="w-16 h-16 rounded-full bg-coral/10 flex items-center justify-center text-xl font-bold text-coral flex-shrink-0" data-id="element-3043">
            {initials(cleaner.name)}
          </span>
          <div className="flex-1 min-w-0" data-id="element-3044">
            <div className="flex items-center gap-2 flex-wrap mb-1" data-id="element-3045">
              <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-3046">
                {cleaner.name}
              </h1>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${meta.cls}`} data-id="element-3047">
                <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} data-id="element-3048" />
                {meta.label}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground" data-id="element-3049">
              <MapPin size={14} className="flex-shrink-0" data-id="element-3050" />
              {cleaner.zones.length > 0 ? cleaner.zones.join(', ') : '—'}
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6" data-id="element-3051">
        <Card data-id="element-3052">
          <CardContent className="text-center" data-id="element-3053">
            <div className="w-9 h-9 rounded-lg bg-coral/10 text-coral flex items-center justify-center mx-auto mb-2" data-id="element-3054">
              <Briefcase size={18} data-id="element-3055" />
            </div>
            <div className="text-2xl font-bold text-navy leading-tight" data-id="element-3056">
              {attivi.length}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5" data-id="element-3057">Attivi</div>
          </CardContent>
        </Card>
        <Card data-id="element-3058">
          <CardContent className="text-center" data-id="element-3059">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-2" data-id="element-3060">
              <CheckCircle2 size={18} data-id="element-3061" />
            </div>
            <div className="text-2xl font-bold text-navy leading-tight" data-id="element-3062">
              {completati.length}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5" data-id="element-3063">
              Completati
            </div>
          </CardContent>
        </Card>
        <Card data-id="element-3064">
          <CardContent className="text-center" data-id="element-3065">
            <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-500 flex items-center justify-center mx-auto mb-2" data-id="element-3066">
              <Star size={18} data-id="element-3067" />
            </div>
            <div className="text-2xl font-bold text-navy leading-tight" data-id="element-3068">
              {avg ?? '—'}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5" data-id="element-3069">
              Score medio
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Assigned jobs */}
      <h2 className="text-sm font-semibold text-navy flex items-center gap-2 mb-3" data-id="element-3070">
        <Briefcase size={16} className="text-coral" data-id="element-3071" />
        Incarichi
      </h2>
      {jobs.length > 0 ? <div className="space-y-3" data-id="element-3072">
          {jobs.map(j => {
        const sm = jobStatusMeta[j.status];
        return <Card key={j.id} className="hover:border-coral/40 hover:shadow-sm transition-all cursor-pointer" onClick={() => navigate(`/cm/clienti/appartamenti/${j.apartmentId}`)} data-id="element-3073">
                <CardContent className="flex items-center justify-between gap-3" data-id="element-3074">
                  <div className="min-w-0" data-id="element-3075">
                    <div className="flex items-center gap-2 mb-1" data-id="element-3076">
                      <span className="text-sm font-semibold text-foreground truncate" data-id="element-3077">
                        {j.apartmentName}
                      </span>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border flex-shrink-0 ${sm.cls}`} data-id="element-3078">
                        {sm.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap" data-id="element-3079">
                      <span className="flex items-center gap-1" data-id="element-3080">
                        <Home size={12} data-id="element-3081" />
                        {j.type}
                      </span>
                      <span className="flex items-center gap-1" data-id="element-3082">
                        <Clock size={12} data-id="element-3083" />
                        {j.date} · {j.time}
                      </span>
                      <span data-id="element-3084">€{j.earning}</span>
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-gray-300 flex-shrink-0" data-id="element-3085" />
                </CardContent>
              </Card>;
      })}
        </div> : <div className="bg-white border border-border rounded-xl p-8 text-center" data-id="element-3086">
          <p className="text-sm text-muted-foreground" data-id="element-3087">
            Nessun incarico assegnato a {cleaner.name}.
          </p>
        </div>}
    </MobileLayout>;
}